import {
  useCallback,
  useEffect,
  useState,
  useMemo,
  type ChangeEvent,
} from 'react';
import { connect, type ConnectedProps } from 'react-redux';
import { Box, Avatar, Checkbox, Typography } from '@mui/material';
import { startCase } from 'lodash/fp';
import { openDrawer } from '@/core/features/drawer/state';
import CustomTable from '@/components/CustomTable';
import SearchField from '@/components/SearchField';
import { sortedMembersByPropertyId, tryFetchMembers } from '@/slices/members';
import { type RootState } from '@/store';
import { type Member } from '@/types';
import { DrawerLayout } from '../../layouts';
import { type DrawerContentProps } from '../../../state';
import styles from './styles';

type Props = DrawerContentProps & ConnectedProps<typeof connector>;

const AddContributorsDrawer = ({
  closeDrawer,
  drawerData,
  members,
  tryFetchMembers,
  openDrawer,
}: Props) => {
  const {
    propertyId,
    currentContributors = [],
    onAddContributors,
    preservedFormState,
    preservedEditorValue,
    sourceDrawerType = 'editEvent',
  } = drawerData || {};

  const [selectedContributors, setSelectedContributors] =
    useState<string[]>(currentContributors);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (propertyId) {
      tryFetchMembers({ propertyId });
    }
  }, [propertyId, tryFetchMembers]);

  useEffect(() => {
    setSelectedContributors(currentContributors || []);
  }, [currentContributors]);

  const filteredMembers = useMemo(
    () =>
      members?.filter((member) =>
        member.full_name?.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [members, searchQuery],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleToggleContributor = (userId: string) => {
    setSelectedContributors((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleAdd = useCallback(() => {
    if (onAddContributors) {
      onAddContributors(selectedContributors);
    }
    closeDrawer();
  }, [selectedContributors, onAddContributors, closeDrawer]);

  const handleBack = useCallback(() => {
    closeDrawer();

    setTimeout(() => {
      openDrawer({
        drawerType: sourceDrawerType,
        drawerData: {
          propertyId,
          preservedFormState,
          preservedEditorValue,
        },
      });
    }, 100);
  }, [
    closeDrawer,
    openDrawer,
    sourceDrawerType,
    propertyId,
    preservedFormState,
    preservedEditorValue,
  ]);

  const formatNameWithCheckbox = (
    cellValue: string,
    columnName: string,
    member: Member,
  ) => {
    const isSelected = selectedContributors.includes(member.user_id);
    return (
      <Box sx={styles.checkboxRow}>
        <Checkbox
          checked={isSelected}
          onChange={() => handleToggleContributor(member.user_id)}
        />

        {member.avatar ? (
          <Box
            component="img"
            sx={styles.avatarImage}
            src={member.avatar}
            alt={member.full_name ?? 'Member'}
          />
        ) : (
          <Avatar sx={styles.avatar}>
            {member.full_name?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
        )}

        <Box>
          <Typography sx={styles.fullName}>{member.full_name}</Typography>
        </Box>
      </Box>
    );
  };

  const HEADERS = ['full_name', 'role'];
  const HEADER_MAPPING = {
    full_name: 'Name',
    role: 'Role',
  };
  const CELL_FORMATTERS = {
    full_name: formatNameWithCheckbox,
    role: startCase,
  };

  return (
    <DrawerLayout
      closeDrawer={closeDrawer}
      title="Add Contributors"
      onAccept={handleAdd}
      acceptText="Add"
      onCancel={closeDrawer}
      cancelText="Cancel"
      onBack={handleBack}
      backText="Back to Event"
    >
      <Box sx={styles.contributorsTableWrapper}>
        <Box sx={styles.searchFieldWrapper}>
          <SearchField
            onChange={handleSearch}
            value={searchQuery}
            placeholder="Search Contributor"
          />
        </Box>

        <CustomTable
          headers={HEADERS}
          headerMapping={HEADER_MAPPING}
          rows={filteredMembers || []}
          cellFormatters={CELL_FORMATTERS}
          actions={[]}
        />
      </Box>
    </DrawerLayout>
  );
};

const connector = connect(
  (state: RootState, ownProps: { drawerData: any }) => ({
    members: sortedMembersByPropertyId(state, {
      propertyId: ownProps.drawerData?.propertyId,
    }),
  }),
  {
    tryFetchMembers,
    openDrawer,
  },
);

export default connector(AddContributorsDrawer);
